import React, { useCallback, useMemo } from 'react';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import type { Currency } from 'api/compensation';
import { formatSubdivisions } from 'api/compensation';

import {
  mapRangeToSliderValue,
  mapSliderValueToRange,
  Slider,
} from 'components/common/Slider/Slider';

const SUBDIVISION_INCREMENT = 100000;

type CompensationSliderProps = {
  label: string | React.ReactNode;
  valueSubdivisions: number;
  setValueSubdivisions?: (valueSubdivisions: number) => void;
  minSubdivisions: number;
  maxSubdivisions: number;
  currency: Currency;
  color?: string;
  editable?: boolean;
};

export function CompensationSlider(props: CompensationSliderProps): JSX.Element {
  const {
    label,
    valueSubdivisions,
    setValueSubdivisions,
    minSubdivisions,
    maxSubdivisions,
    currency,
    color,
    editable = false,
  } = props;

  const updateValueSubdivisions = useCallback(
    (value: number): void => {
      if (!setValueSubdivisions) {
        return;
      }

      setValueSubdivisions(mapSliderValueToRange(value, minSubdivisions, maxSubdivisions));
    },
    [setValueSubdivisions, minSubdivisions, maxSubdivisions],
  );

  const incrementSliderValue = useMemo(() => {
    if (minSubdivisions === maxSubdivisions) {
      return undefined;
    }
    return SUBDIVISION_INCREMENT / (maxSubdivisions - minSubdivisions);
  }, [minSubdivisions, maxSubdivisions]);

  return (
    <Flex className="slider-container" direction="column" gap="lg">
      <Flex className="slider-header" direction="row" justify="space-between">
        <Typography className="slider-label" variant="subtitle">
          {label}
        </Typography>
        <Typography className="slider-value" variant="header">
          <span style={{ color }}>{formatSubdivisions(valueSubdivisions, currency)}</span>
        </Typography>
      </Flex>
      <Flex className="slider-body" direction="column" gap="md">
        <Slider
          value={mapRangeToSliderValue(valueSubdivisions, minSubdivisions, maxSubdivisions)}
          minIncrement={mapRangeToSliderValue(
            Math.ceil(minSubdivisions / SUBDIVISION_INCREMENT) * SUBDIVISION_INCREMENT,
            minSubdivisions,
            maxSubdivisions,
          )}
          maxIncrement={mapRangeToSliderValue(
            Math.floor(maxSubdivisions / SUBDIVISION_INCREMENT) * SUBDIVISION_INCREMENT,
            minSubdivisions,
            maxSubdivisions,
          )}
          increment={incrementSliderValue}
          setValue={updateValueSubdivisions}
          editable={editable}
          color={color}
        />
        <Flex className="slider-range" direction="row" justify="space-between">
          <Typography className="minimum-value" variant="caption">
            {formatSubdivisions(minSubdivisions, currency)}
          </Typography>
          <Typography className="maximum-value" variant="caption">
            {formatSubdivisions(maxSubdivisions, currency)}
          </Typography>
        </Flex>
      </Flex>
    </Flex>
  );
}
